import type { PlaygroundMessage } from "@/stores/PlaygroundStore";

import React, { useEffect, useRef, useMemo } from "react";
import { Button, Chip, ScrollShadow } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import DOMPurify from "dompurify";

import { usePlaygroundStore } from "@/stores/PlaygroundStore";
import { useTheme } from "@/hooks/use-theme";

const MessageCard: React.FC<{ message: PlaygroundMessage }> = ({ message }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const statusColour =
    message.status === "success"
      ? "success"
      : message.status === "failed"
        ? "danger"
        : message.status === "stopped"
          ? "warning"
          : "primary";

  const renderedContent = useMemo(() => {
    if (!message.content) return "";

    return DOMPurify.sanitize(marked.parse(message.content) as string);
  }, [message.content]);

  return (
    <div
      className={`
        rounded-lg border p-3 mb-2 transition-all duration-200
        ${isDark ? "bg-default-100/50 border-default-200" : "bg-white border-default-200"}
      `}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon
            className={`w-4 h-4 ${message.role === "user" ? "text-blue-500" : "text-purple-500"}`}
            icon={message.role === "user" ? "solar:user-linear" : "solar:user-hand-up-outline"}
          />
          <span className="text-xs font-medium text-default-600">
            {message.agent || (message.role === "user" ? "User" : "Assistant")}
          </span>
          <Chip className="text-tiny" color={statusColour} size="sm" variant="flat">
            {message.status === "streaming" ? t("Streaming...") : message.status}
          </Chip>
        </div>
        {message.usage && (
          <span className="text-tiny text-default-400">{message.usage.totalTokens} tokens</span>
        )}
      </div>
      <div className="text-sm text-default-700 prose prose-sm dark:prose-invert max-w-none">
        {renderedContent ? (
          <div dangerouslySetInnerHTML={{ __html: renderedContent }} />
        ) : message.status === "streaming" ? (
          <span className="text-default-400 animate-pulse">{t("Generating...")}</span>
        ) : null}
      </div>
    </div>
  );
};

const PlaygroundMessagesPanel: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { executionMessages, isMessagesPanelOpen, toggleMessagesPanel, isRunning } =
    usePlaygroundStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [executionMessages]);

  if (!isMessagesPanelOpen) {
    return null;
  }

  return (
    <div
      className={`
        border-t transition-all duration-300
        ${isDark ? "bg-default-50 border-default-200" : "bg-gray-50 border-default-200"}
      `}
      style={{ height: "280px" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-default-200">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-default-500" icon="solar:chat-square-linear" />
          <span className="text-sm font-medium text-default-700">{t("Execution Output")}</span>
          {executionMessages.length > 0 && (
            <Chip className="text-tiny" color="default" size="sm" variant="flat">
              {executionMessages.length}
            </Chip>
          )}
          {isRunning && (
            <Icon
              className="w-3.5 h-3.5 text-primary animate-spin"
              icon="solar:refresh-circle-linear"
            />
          )}
        </div>
        <Button
          isIconOnly
          aria-label={t("Close panel")}
          size="sm"
          variant="light"
          onPress={toggleMessagesPanel}
        >
          <Icon className="w-4 h-4" icon="solar:minimize-square-linear" />
        </Button>
      </div>

      {/* Messages area */}
      <ScrollShadow ref={scrollRef} className="h-[calc(100%-40px)] px-4 py-2 overflow-y-auto">
        {executionMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-default-400">
            <Icon className="w-8 h-8 mb-2" icon="solar:chat-round-dots-linear" />
            <p className="text-sm">{t("Run a workflow to see output here")}</p>
          </div>
        ) : (
          executionMessages.map((msg) => <MessageCard key={msg.id} message={msg} />)
        )}
      </ScrollShadow>
    </div>
  );
};

export default PlaygroundMessagesPanel;
